import { compulsoryColumns, excludedColumns } from "@Components/FileColumnsTreeView/constants";
import axiosWrapper from "@AxiosWrapper";
import type { GetDistinctFilePropertyNamesResponse } from "@ServerTypes/queries/queries.schema";
import { getBackendUrl } from "@Utils";

/** A grid column referenced either by its plain field name or by a definition that carries one. */
export type ColumnRef = string | { field?: unknown };

/**
 * Drops the excluded and compulsory columns. This copy is only fed the property-name list from
 * `getFilePropertyNames` below, so it is plain `string[]` in and out.
 */
export const getFilteredColumns = (columns: string[]): string[] =>
  columns.filter((col: ColumnRef) => {
    // `col` is either the field name itself or a column definition holding it.
    let column: string = col as string;
    if (typeof column != "string") {
      column = (col as { field: string }).field;
    }
    let includeColumn = true;

    excludedColumns.forEach((excludedColumn) => {
      if (excludedColumn.field == column && includeColumn) {
        includeColumn = false;
      }
    });

    compulsoryColumns.forEach((compulsoryColumn) => {
      if (compulsoryColumn.field == column && includeColumn) {
        includeColumn = false;
      }
    });

    return includeColumn;
  });

export const getFilePropertyNames = async (fileVersionId: string | string[]) => {
  const propertyNames = await axiosWrapper.post<GetDistinctFilePropertyNamesResponse>(
    `${getBackendUrl()}/queries/getDistinctFilePropertyNames`,
    {
      fileVersionId: Array.isArray(fileVersionId) ? fileVersionId : [fileVersionId],
    },
  );

  let columns: string[] = [];
  if (propertyNames.data.status) {
    // SCHEMA GAP: getDistinctFilePropertyNamesResponse declares the names under `result`, but the
    // handler returns them under an undeclared `data` key.
    columns = getFilteredColumns(
      (propertyNames.data as GetDistinctFilePropertyNamesResponse & { data: string[] }).data,
    );
  }
  return [columns, propertyNames.data];
};

export const convertToDotNotation = (
  obj: Record<string, unknown>,
  parentKey = "",
  result: Record<string, unknown> = {},
) => {
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      const checkForObjectInCustomFields = typeof obj[key] === "object" && parentKey != "customProperties";

      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key]) &&
        checkForObjectInCustomFields
      ) {
        convertToDotNotation(obj[key] as Record<string, unknown>, newKey, result);
      } else {
        result[newKey] = obj[key];
      }
    }
  }
  return result;
};

/**
 * Flattens the merged model-properties map `{ [fileVersionId]: { [nodeId]: node } }` to grid rows.
 * Callers hold that map under several node types (ModelPropertyNode, MergedNode), so the per-file
 * bucket is only `object` here and each node is read as an open record.
 */
export const getModifiedNodesList = (modelProperties: Record<string, object>): Record<string, unknown>[] => {
  return Object.values(modelProperties)
    .flatMap((v) => Object.values(v) as Record<string, unknown>[])
    .map((data, index: number) => ({
      // nodes without _id would all share id:undefined and break the DataView
      id:
        data?.fileVersionId != null && data?.nodeId != null ? `${data.fileVersionId}_${data.nodeId}` : `node_${index}`,
      ...convertToDotNotation(data),
    }));
};

// KNOWN BUG, preserved verbatim: unlike the SearchSet copy of this helper, this one has no
// null guard, so a rule with no field selected throws here.
export const extractPropertyPartialName = (field: string) => {
  const rootProperty = field.split(".");
  const value = rootProperty.slice(1).join(".").split("/");
  if (value.length > 1) {
    return value.slice(1).join("/");
  }
  // split() always yields at least one element.
  return value[0]!;
};
